import {
  Component,
  OnInit,
  Input,
  ContentChildren,
  QueryList,
  HostBinding,
  Output,
  EventEmitter,
} from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
// models
import { Tab, TabIdList } from "../../../models/tab.model";
// providers
import { TabSingleComponent } from "./tab-single/tab-single.component";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import {
  ui_activeTabs,
  ui_tabIdList,
} from "src/app/shared/ngrx/ngrx-selectors";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import {
  GO_TO_CLINIC_KPI,
  UI__ADD_TO_TAB_STORE,
  UI__HIGHLIGHT_TABS,
  UI__REMOVE_FROM_TAB_STORE,
  UI__SET_ACTIVE_TABS,
  UI__TAB_NAVIGATION,
} from "src/app/shared/ngrx/ngrx-actions";
import { TabButtonComponent } from "./tab-button/tab-button.component";
import { CommonModule } from "@angular/common";

/**
 * A tab can point to a component either as parent via the ng-content directive
 * or by routing via the 'link' property.
 */
@Component({
  standalone: true,
  imports: [TabButtonComponent, CommonModule, RouterOutlet],
  selector: "tabs",
  template: `
    <div class="h-full g-tabSet" [class.has-topStroke]="topStroke">
      <nav role="tablist" class="l-align-bottom l-flex-wrap t-tablist">
        <span
          *ngFor="let tab of localTabList"
          class="l-block l-pos-relative tab-button-wrapper"
          (mouseenter)="emitMouseEnter(tab)"
          (mouseleave)="emitMouseLeave(tab)"
        >
          <tab-button
            [tab]="tab"
            (onTabClick)="setActiveTabOnClick($event)"
          ></tab-button>
          <ng-container *ngTemplateOutlet="tab.subMenu"></ng-container>
        </span>
      </nav>
      <div #tabsHolder class="t-tabContainer" scrollable>
        <router-outlet></router-outlet>
        <ng-container *ngIf="!byRoute || !removeRoute">
          <ng-content></ng-content>
        </ng-container>
      </div>
    </div>
  `,
})
export class TabsComponent implements OnInit {
  activeTab: Tab;
  activeIds: string[] = [];
  localTabList: Tab[];
  activeTabList: Tab[] = [];
  tabStoreKey: string;
  tabStoreUpdate: { currentTab };
  destroy$ = new Subject();
  tabIdList: TabIdList;

  @Input() defaultTab: string;
  @Input() hFullAll: boolean;
  @Input() topStroke: boolean;
  @Input() removeRoute: boolean = false;
  @Output() onMouseEnter = new EventEmitter<Function>();
  @Output() onMouseLeave = new EventEmitter<Function>();
  @ContentChildren(TabSingleComponent) tabs: QueryList<TabSingleComponent>;
  @HostBinding("class") get hFull() {
    if ((this.activeTab && this.activeTab.hFull) || this.hFullAll) {
      return "l-flex-column h-full t-preventScroll";
    }
    return "";
  }
  get byRoute(): boolean {
    return this.activeTab && !!this.activeTab.link;
  }
  constructor(
    private readonly store: Store,
    private actions$: Actions,
    private router: Router
  ) {}

  ngOnInit() {
    this.store
      .select(ui_tabIdList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sm) => {
        this.tabIdList = sm;
      });

    this.store
      .select(ui_activeTabs)
      .pipe(takeUntil(this.destroy$))
      .subscribe((sm) => {
        this.activeTabList = sm;
      });

    this.actions$
      .pipe(
        ofType(UI__SET_ACTIVE_TABS),
        takeUntil(this.destroy$),
        map(({}) => {
          this.navigateByTab(this.activeTabList[0]);
          if (!this.localTabList.find((e) => e.isActive)) {
            this.setDefaultTab();
          }
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(GO_TO_CLINIC_KPI),
        takeUntil(this.destroy$),
        map(({ clinicId }) => {
          if (clinicId) {
            let tab = this.tabs.find(
              (t) => t.tabId == this.tabIdList.KPI__CLINICS
            );
            if (tab) this.navigateByTab(tab);
          }
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(UI__HIGHLIGHT_TABS),
        takeUntil(this.destroy$),
        map(({}) => {
          this.updateTabs();
        })
      )
      .subscribe();
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.addToTabStore();
      this.setDefaultTab();
    });
  }

  ngOnDestroy() {
    this.removeFromTabStore();
    this.destroy$.next(1);
  }

  updateTabs() {
    this.localTabList.forEach((tab) => {
      const match = this.activeTabList.find((e) => e.tabId === tab.tabId);
      if (match) {
        Object.assign(tab, match);
      }
    });
  }

  setActiveTabOnClick(tab: Tab) {
    this.localTabList.forEach((localTab) => {
      localTab.isActive = false;
    });
    tab.isActive = true;
    this.navigateByTab(tab);
    if (tab.isActive && typeof tab.onSelect === "function") tab.onSelect(tab);
  }

  setActiveTabByNavigation(tab: Tab) {
    this.localTabList.forEach((localTab) => {
      const cond = localTab.link === tab.link;
      localTab.isActive = cond;
      if (cond) this.activeTab = tab;
    });
    tab.disabled = false;
  }

  setActiveTabByTabId(tab: Tab) {
    this.localTabList.forEach((localTab) => {
      if (localTab.tabId === tab.tabId) {
        this.localTabList.forEach((lt) => {
          lt.isActive = false;
        });
        localTab.isActive = true;
        this.activeTab = tab;
      }
    });
  }

  navigateByTab(tab: Tab) {
    requestAnimationFrame(() => {
      this.store.dispatch(
        UI__TAB_NAVIGATION({
          data: tab,
        })
      );
    });
    if (tab.link) {
      this.router.navigate([tab.link]);
      this.setActiveTabByNavigation(tab);
    } else if (tab.tabId) {
      this.setActiveTabByTabId(tab);
    }
    // this.tabStoreUpdate.currentTab = tab;
  }

  private addToTabStore() {
    // should only be called after view init
    this.localTabList = this.tabs.toArray();
    this.tabStoreKey = Math.random().toString();

    this.store.dispatch(
      UI__ADD_TO_TAB_STORE({
        tabStoreKey: this.tabStoreKey,
        tabs: this.localTabList,
      })
    );
  }

  private removeFromTabStore() {
    // should only be called on destroy
    this.store.dispatch(
      UI__REMOVE_FROM_TAB_STORE({
        tabStoreKey: this.tabStoreKey,
      })
    );
  }

  setDefaultTab() {
    let defaultItem;
    if (this.defaultTab) {
      defaultItem = this.localTabList.find((item) => {
        return item.tabId === this.defaultTab;
      });
    } else {
      defaultItem = this.localTabList[0];
    }
    this.setActiveTabOnClick(defaultItem);
  }

  dispatchTab(tab: Tab) {
    this.store.dispatch(
      UI__SET_ACTIVE_TABS({
        data: [tab],
      })
    );
  }

  emitMouseEnter(tab: Tab) {
    this.onMouseEnter.emit(tab.onMouseEnter);
    tab.onMouseEnter && tab.onMouseEnter(tab);
  }

  emitMouseLeave(tab: Tab) {
    this.onMouseLeave.emit(tab.onMouseLeave);
    tab.onMouseLeave && tab.onMouseLeave(tab);
  }

  refreshTabsToStore() {
    this.removeFromTabStore();
    this.addToTabStore();
  }
}
